/**
 * Deslizar con el ratón una fila que se desplaza en horizontal.
 *
 * Con el dedo ya se desliza sola; con un ratón, una fila sin barra (las
 * tarjetas de «Primeros pasos», por ejemplo) sólo se movía con las flechas, y
 * en una ventana estrecha no había ni eso. Se agarra y se arrastra. Mientras se
 * arrastra se apaga el ajuste a tarjeta (`scroll-snap`), que pelearía con cada
 * píxel; al soltar vuelve y la fila se asienta en la tarjeta más cercana. Un
 * arrastre no cuenta como clic: soltar encima de un botón no lo pulsa.
 */
import { useEffect, type RefObject } from 'react'

const THRESHOLD = 5

export function useDragScroll(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const rail = ref.current
    if (!rail) return
    let startX = 0
    let startScroll = 0
    let pointerId: number | null = null
    let dragging = false

    const onDown = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return
      if (rail.scrollWidth <= rail.clientWidth) return
      pointerId = event.pointerId
      startX = event.clientX
      startScroll = rail.scrollLeft
      dragging = false
    }
    const onMove = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return
      const delta = event.clientX - startX
      if (!dragging) {
        if (Math.abs(delta) < THRESHOLD) return
        dragging = true
        rail.setPointerCapture(event.pointerId)
        rail.style.scrollSnapType = 'none'
        rail.classList.add('is-drag-scrolling')
      }
      rail.scrollLeft = startScroll - delta
    }
    const end = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return
      pointerId = null
      if (!dragging) return
      rail.classList.remove('is-drag-scrolling')
      rail.style.scrollSnapType = ''
      /* El clic que llega justo después del arrastre se descarta, una vez. */
      const swallow = (click: MouseEvent) => { click.preventDefault(); click.stopPropagation() }
      rail.addEventListener('click', swallow, { capture: true, once: true })
      window.setTimeout(() => rail.removeEventListener('click', swallow, { capture: true }), 0)
    }

    rail.addEventListener('pointerdown', onDown)
    rail.addEventListener('pointermove', onMove)
    rail.addEventListener('pointerup', end)
    rail.addEventListener('pointercancel', end)
    return () => {
      rail.removeEventListener('pointerdown', onDown)
      rail.removeEventListener('pointermove', onMove)
      rail.removeEventListener('pointerup', end)
      rail.removeEventListener('pointercancel', end)
    }
  }, [ref])
}
